import { useCallback, useEffect, useState } from 'react';
import type { Nullable } from 'corgi-types';

import { RangeSliderElement, RangeSliderInteraction } from '../../tracking/filters';
import type { DispatchState } from '../../types/React';

interface RangeParams {
  min: number;
  max: number;
  selectedMin: Nullable<number>;
  selectedMax: Nullable<number>;
}

export interface UseRangeSlidersParams extends RangeParams {
  withApplyButton?: boolean;
  onRangeChange: (value: number[]) => void;
  trackRangeChange?: (element: RangeSliderElement, interaction: RangeSliderInteraction) => void;
}

interface UseHandlersParams extends UseRangeSlidersParams {
  currentValues: number[];
  setCurrentValues: DispatchState<number[]>;
}

const getRange = ({ min, max, selectedMin, selectedMax }: RangeParams) => [
  selectedMin ? Math.min(selectedMin, min) : min,
  Math.max(selectedMax, max),
];

const getSelectedRange = ({ min, max, selectedMin, selectedMax }: RangeParams) => [
  selectedMin || min,
  selectedMax || max,
];

const useRangeHandlers = ({
  currentValues,
  setCurrentValues,
  trackRangeChange,
  onRangeChange,
  withApplyButton,
}: UseHandlersParams) => {
  const [isMinHandleDragging, setIsMinHandleDragging] = useState(false);
  const [isMaxHandleDragging, setIsMaxHandleDragging] = useState(false);
  const [currentMin] = currentValues;

  const handleRangeOnChange = useCallback(
    (value: number[]) => {
      const [minValue] = value;

      setCurrentValues(value);

      return minValue !== currentMin ? setIsMinHandleDragging(true) : setIsMaxHandleDragging(true);
    },
    [currentMin]
  );

  const handleRangeOnAfterChange = useCallback(() => {
    if (isMinHandleDragging || isMaxHandleDragging) {
      trackRangeChange(
        RangeSliderElement.Slider,
        isMinHandleDragging ? RangeSliderInteraction.Min : RangeSliderInteraction.Max
      );
    }
    setIsMinHandleDragging(false);
    setIsMaxHandleDragging(false);
    if (!withApplyButton) {
      onRangeChange(currentValues);
    }
  }, [currentValues, onRangeChange, trackRangeChange, withApplyButton]);

  return {
    isMinHandleDragging,
    isMaxHandleDragging,
    handleRangeOnChange,
    handleRangeOnAfterChange,
  };
};

const useMaxInputHandler = ({
  currentValues,
  setCurrentValues,
  withApplyButton,
  trackRangeChange,
  onRangeChange,
  ...rangeParams
}: UseHandlersParams) => {
  const [currentMin] = currentValues;
  const max = getRange(rangeParams)[1];

  return useCallback(
    (value: string) => {
      const newMax = +value;
      let newValues: [number, number];
      if (newMax > max) {
        newValues = [currentMin, max];
      } else if (newMax < currentMin) {
        newValues = [currentMin, currentMin];
      } else {
        newValues = [currentMin, newMax];
      }
      setCurrentValues(newValues);
      trackRangeChange(RangeSliderElement.Input, RangeSliderInteraction.Max);
      if (!withApplyButton) {
        onRangeChange(newValues);
      }
    },
    [max, currentValues, onRangeChange, trackRangeChange, withApplyButton]
  );
};

const useMinInputHandler = ({
  currentValues,
  setCurrentValues,
  withApplyButton,
  trackRangeChange,
  onRangeChange,
  ...rangeParams
}: UseHandlersParams) => {
  const currentMax = currentValues[1];
  const [min] = getRange(rangeParams);

  return useCallback(
    (value: string) => {
      const newMin = +value;
      let newValues: [number, number];
      if (newMin < min) {
        newValues = [min, currentMax];
      } else if (newMin > currentMax) {
        newValues = [currentMax, currentMax];
      } else {
        newValues = [newMin, currentMax];
      }
      setCurrentValues(newValues);
      trackRangeChange(RangeSliderElement.Input, RangeSliderInteraction.Min);
      if (!withApplyButton) {
        onRangeChange(newValues);
      }
    },
    [min, currentValues, onRangeChange, trackRangeChange, withApplyButton]
  );
};

const useRangeValues = (params: UseRangeSlidersParams) => {
  const { selectedMin, selectedMax, max: initialMax, min: initialMin } = params;
  const [min, max] = getRange(params);
  const [currentValues, setCurrentValues] = useState(
    getSelectedRange({ selectedMin, selectedMax, min, max })
  );

  useEffect(() => {
    setCurrentValues(getSelectedRange({ selectedMin, selectedMax, min, max }));
  }, [initialMin, initialMax, selectedMin, selectedMax]);

  return { min, max, currentValues, setCurrentValues };
};

export const useRangeSlider = (params: UseRangeSlidersParams) => {
  const { min, max, currentValues, setCurrentValues } = useRangeValues(params);
  const handlerParams = { currentValues, setCurrentValues, ...params };
  const rangeHandlers = useRangeHandlers(handlerParams);

  const handleApply = useCallback(
    event => {
      event.preventDefault();
      params.onRangeChange(currentValues);
    },
    [currentValues, params.onRangeChange]
  );

  return {
    currentValues,
    min,
    max,
    handleApply,
    onMinInputChange: useMinInputHandler(handlerParams),
    onMaxInputChange: useMaxInputHandler(handlerParams),
    ...rangeHandlers,
  };
};
